import { mapSliderProductApiData } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    ProductRecommendationAreaFragmentApi,
    useAddToCartPopupRecommendationAreasQueryApi,
    useCartPageRecommendationAreasQueryApi,
} from 'graphql/generated';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { ProductRecommendationAreaType } from 'types/productRecommendationArea';

export const useAddToCartPopupRecommendationAreas = (
    productUuid: string,
): ProductRecommendationAreaType[] | undefined => {
    const { wishlist } = useWishlist();
    const { currencyCode } = useDomainConfig();

    const useWishlistVariable = (wishlist?.products.length ?? 0) > 0;

    const [{ data, error, fetching }] = useAddToCartPopupRecommendationAreasQueryApi({
        variables: {
            productUuid: productUuid,
            wishlistUuid: useWishlistVariable ? wishlist?.uuid ?? null : null,
        },
        requestPolicy: useWishlistVariable ? 'network-only' : 'cache-first',
    });

    useQueryError(error);

    const recommendationAreas = useMemo(
        () =>
            data?.addToCartPopupRecommendationAreas
                ? prepareProductRecommendationAreasForDisplay(
                      mapProductRecommendationAreas(data.addToCartPopupRecommendationAreas, currencyCode),
                  )
                : [],
        [currencyCode, data?.addToCartPopupRecommendationAreas],
    );

    if (fetching) {
        return undefined;
    }

    return recommendationAreas;
};

export const useCartPageRecommendationAreas = (): ProductRecommendationAreaType[] | undefined => {
    const { wishlist } = useWishlist();
    const { cartUuid } = usePersistStore((s) => s);
    const { currencyCode } = useDomainConfig();

    const [{ data, error, fetching }] = useCartPageRecommendationAreasQueryApi({
        variables: {
            wishlistUuid: wishlist?.uuid ?? null,
            cartUuid: cartUuid,
        },
    });

    useQueryError(error);

    const recommendationAreas = useMemo(
        () =>
            data?.cartPageRecommendationAreas
                ? prepareProductRecommendationAreasForDisplay(
                      mapProductRecommendationAreas(data.cartPageRecommendationAreas, currencyCode),
                  )
                : [],
        [currencyCode, data?.cartPageRecommendationAreas],
    );

    if (fetching) {
        return undefined;
    }

    return recommendationAreas;
};

export const mapProductRecommendationAreas = (
    apiData: ProductRecommendationAreaFragmentApi[],
    currencyCode: string,
): ProductRecommendationAreaType[] => {
    const mappedAreas = [];

    for (const recommendationArea of apiData) {
        mappedAreas.push({
            area: {
                header: recommendationArea.area.header,
                externalSourceDivId: recommendationArea.area.externalSourceDivId ?? null,
                gtmId: recommendationArea.area.gtmId ?? null,
                maximumLoadedProductCount: recommendationArea.area.maximumLoadedProductCount,
                randomizeProducts: recommendationArea.area.randomizeProducts,
            },
            products: recommendationArea.products
                ? mapSliderProductApiData(recommendationArea.products, currencyCode)
                : [],
        });
    }

    return mappedAreas;
};

export const useDisplayedProductRecommendationAreas = (
    productRecommendationAreas: ProductRecommendationAreaType[],
    prepareProductOrderOnMount: boolean,
): {
    productRecommendationAreasForDisplay: ProductRecommendationAreaType[];
    isProductOrderPending: boolean;
} => {
    const shouldPrepareProductOrder =
        prepareProductOrderOnMount &&
        productRecommendationAreas.some(
            (productRecommendationArea) =>
                productRecommendationArea.area.randomizeProducts && productRecommendationArea.products.length > 0,
        );
    const [preparedProductRecommendationAreas, setPreparedProductRecommendationAreas] =
        useState<ProductRecommendationAreaType[]>();
    const hasPreparedProductOrder = useRef(false);

    useEffect(() => {
        if (!shouldPrepareProductOrder || hasPreparedProductOrder.current) {
            return;
        }

        hasPreparedProductOrder.current = true;
        setPreparedProductRecommendationAreas(prepareProductRecommendationAreasForDisplay(productRecommendationAreas));
    }, [productRecommendationAreas, shouldPrepareProductOrder]);

    return {
        productRecommendationAreasForDisplay: shouldPrepareProductOrder
            ? preparedProductRecommendationAreas ?? []
            : productRecommendationAreas,
        isProductOrderPending: shouldPrepareProductOrder && preparedProductRecommendationAreas === undefined,
    };
};

export const prepareProductRecommendationAreasForDisplay = (
    productRecommendationAreas: ProductRecommendationAreaType[],
): ProductRecommendationAreaType[] =>
    productRecommendationAreas.map((productRecommendationArea) => {
        const products = productRecommendationArea.area.randomizeProducts
            ? shuffle(productRecommendationArea.products)
            : productRecommendationArea.products;

        return {
            ...productRecommendationArea,
            products: products.slice(0, productRecommendationArea.area.maximumLoadedProductCount),
        };
    });

const shuffle = <T>(items: T[]): T[] => {
    const shuffledItems = [...items];

    for (let index = shuffledItems.length - 1; index > 0; index--) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffledItems[index], shuffledItems[randomIndex]] = [shuffledItems[randomIndex], shuffledItems[index]];
    }

    return shuffledItems;
};
